/**
 * Groups marketplace audiences by their tier-1 industry label.
 *
 * Shared between the audience lobby (useAudiencesData) and the study
 * creation wizard (AudienceStep) so both surfaces show identical rows.
 *
 * Grouping precedence:
 *  1. industryLabels[tier=1] — preferred; comes from the multi-label classifier
 *  2. industryLabel.fullPath root — legacy single-label fallback
 *  3. "Other" — catch-all for unlabelled audiences
 */

import type { IndustryData } from '@/pages/AudiencesPage/components/IndustrySection'

export interface IndustryGroup<T> {
  industry: IndustryData
  audiences: Array<T>
}

interface WithIndustryFields {
  sharingMode?: string
  industryLabels?: Array<{ token: string; name: string; tier: number }> | null
  industryLabel?: { name: string; fullPath?: string } | null
}

const OTHER_KEY = '__other__'

export function groupMarketplaceByIndustry<T extends WithIndustryFields>(
  audiences: Array<T>,
): Array<IndustryGroup<T>> {
  const marketplace = audiences.filter((a) => a.sharingMode === 'marketplace')

  const byIndustry = new Map<string, { name: string; audiences: Array<T> }>()

  for (const a of marketplace) {
    const labels = a.industryLabels ?? []
    const tier1 = labels.find((l) => l.tier === 1)

    if (tier1) {
      const entry = byIndustry.get(tier1.token) ?? { name: tier1.name, audiences: [] }
      entry.audiences.push(a)
      byIndustry.set(tier1.token, entry)
    } else if (a.industryLabel?.name) {
      const fullPath: string = a.industryLabel.fullPath ?? a.industryLabel.name
      const rootName = fullPath.split(' | ')[0] ?? a.industryLabel.name
      const syntheticKey = `__industry_${rootName.toLowerCase().replace(/\s+/g, '_')}__`
      const entry = byIndustry.get(syntheticKey) ?? { name: rootName, audiences: [] }
      entry.audiences.push(a)
      byIndustry.set(syntheticKey, entry)
    } else {
      const entry = byIndustry.get(OTHER_KEY) ?? { name: 'Other', audiences: [] }
      entry.audiences.push(a)
      byIndustry.set(OTHER_KEY, entry)
    }
  }

  return Array.from(byIndustry.entries())
    .sort((a, b) => {
      if (a[0] === OTHER_KEY) return 1
      if (b[0] === OTHER_KEY) return -1
      return a[1].name.localeCompare(b[1].name, undefined, { sensitivity: 'base' })
    })
    .map(([token, { name, audiences: industryAudiences }]) => ({
      industry: { token, name },
      audiences: industryAudiences,
    }))
}
